/**
 * ValidationModal — the SACRED moment of human-in-the-loop.
 *
 * Every irreversible action (send email · post · push code · charge) lands
 * here. Three buttons:
 *   · CANCEL    — destroy the artifact, task back to backlog
 *   · MODIFY    — continue editing with the agent (inline chat)
 *   · VALIDATE  — fire the real-world side-effect (or just mark done)
 *
 * Coherent with Workshop design.
 */
import React, { useState } from 'react';
import { X, CheckCircle2, Edit3, Loader2, Send, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AGENT_META = {
  aria:   { name: 'Aria',   role: 'The Architect',   accent: '#7C3AED', soft: '#A78BFA', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)' },
  lyra:   { name: 'Lyra',   role: 'The Storyteller', accent: '#FF6B1A', soft: '#FFD166', gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)' },
  hermes: { name: 'Hermes', role: 'The Pathfinder',  accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)' },
};
const normalizeOwner = (o) => {
  const x = String(o || '').toLowerCase();
  if (['markus', 'marcus', 'revenue', 'sales', 'growth'].includes(x)) return 'hermes';
  if (['builder', 'aria'].includes(x)) return 'aria';
  if (['content', 'lyra'].includes(x)) return 'lyra';
  return 'hermes';
};
const ACTION_LABEL = {
  send_email:      'Validate & Send',
  post_social:     'Validate & Post',
  push_code:       'Validate & Push',
  charge:          'Validate & Charge',
  add_to_pipeline: 'Validate & Add',
  none:            'Validate',
};

export default function ValidationModal({ task, onClose, onDone }) {
  const [modifyInstruction, setModifyInstruction] = useState('');
  const [task_, setTask] = useState(task);
  const [busy, setBusy] = useState(null); // 'cancel' | 'modify' | 'validate'
  const [confirmStep, setConfirmStep] = useState(false);
  const [error, setError] = useState(null);

  const agent = normalizeOwner(task_.owner_agent_id);
  const meta = AGENT_META[agent];
  const artifact = task_.artifact || {};
  const content = artifact.content;
  const contentText = typeof content === 'string'
    ? content
    : content
      ? JSON.stringify(content, null, 2)
      : '(No artifact content yet — the agent has not delivered.)';

  const action = task_.validation_action || 'none';
  const irreversible = !!task_.irreversible;

  const doCancel = async () => {
    setBusy('cancel');
    setError(null);
    const r = await fetch(`${API}/api/tasks/${task_.task_id}/cancel`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true }),
    });
    setBusy(null);
    if (r.ok) onDone();
    else setError(`Cancel failed (HTTP ${r.status})`);
  };

  const doModify = async () => {
    if (!modifyInstruction.trim() || busy) return;
    setBusy('modify');
    setError(null);
    const r = await fetch(`${API}/api/tasks/${task_.task_id}/modify`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction: modifyInstruction.trim() }),
    });
    setBusy(null);
    if (r.ok) {
      const data = await r.json();
      setTask(prev => ({ ...prev, artifact: data.artifact }));
      setModifyInstruction('');
    } else {
      setError(`Modify failed (HTTP ${r.status})`);
    }
  };

  const doValidate = async () => {
    if (irreversible && !confirmStep) {
      setConfirmStep(true);
      return;
    }
    setBusy('validate');
    setError(null);
    const r = await fetch(`${API}/api/tasks/${task_.task_id}/validate`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true }),
    });
    setBusy(null);
    if (r.ok) onDone();
    else setError(`Validation failed (HTTP ${r.status})`);
  };

  return (
    <div
      data-testid="validation-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(15,15,30,0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 1100, maxHeight: '90vh',
        background: '#FFFFFF', borderRadius: 18,
        boxShadow: '0 24px 80px rgba(15,15,30,0.4)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid rgba(15,15,30,0.06)',
          background: '#FBFAF7',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: meta.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#FFFFFF', fontWeight: 800, fontSize: 14,
          }}>
            {meta.name[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
              color: meta.accent, textTransform: 'uppercase',
            }}>{meta.name} delivered</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F1A', marginTop: 2 }}>
              {task_.title}
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
              Ready for your validation · acceptance: {task_.acceptance_criteria || task_.description || '—'}
            </div>
          </div>
          <button data-testid="validation-close-btn" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={20} color="#6B7280" />
          </button>
        </div>

        {/* Body — 2 columns */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.6fr 1fr', minHeight: 0 }}>
          {/* LEFT: artifact preview — smart renderer per artifact.kind */}
          <div style={{
            padding: 22, overflowY: 'auto',
            borderRight: '1px solid rgba(15,15,30,0.06)',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: '#6B7280', marginBottom: 10,
            }}>Artifact preview · {artifact.kind || 'unknown'}</div>
            <ArtifactBody artifact={artifact} contentText={contentText} accent={meta.accent} />
            {Array.isArray(artifact.versions) && artifact.versions.length > 0 && (
              <div style={{ marginTop: 14, fontSize: 11, color: '#6B7280' }}>
                {artifact.versions.length} previous version{artifact.versions.length > 1 ? 's' : ''} (after founder modifications)
              </div>
            )}
          </div>

          {/* RIGHT: inline chat */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid rgba(15,15,30,0.06)',
              background: '#FBFAF7',
            }}>
              <div style={{
                fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
                color: meta.accent, textTransform: 'uppercase',
              }}>Refine with {meta.name}</div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                Type a modification — {meta.name} updates the artifact above.
              </div>
            </div>
            <div style={{ flex: 1, padding: '16px 18px', overflowY: 'auto', fontSize: 12, color: '#6B7280' }}>
              <div style={{ fontStyle: 'italic' }}>
                Examples ·
                {agent === 'hermes' && ' "shorter and warmer", "remove the price mention", "add a specific reference to their last LinkedIn post"'}
                {agent === 'lyra' && ' "more on-brand for our cosmic theme", "punchier hook", "swap the CTA to a question"'}
                {agent === 'aria' && ' "use bcrypt instead of argon2", "add error handling for the 401 case", "split into 2 endpoints"'}
              </div>
            </div>
            <div style={{ padding: 14, borderTop: '1px solid rgba(15,15,30,0.06)' }}>
              <textarea
                data-testid="validation-modify-input"
                value={modifyInstruction}
                onChange={e => setModifyInstruction(e.target.value)}
                placeholder={`Tell ${meta.name} what to change…`}
                rows={3}
                style={{
                  width: '100%', padding: 12, borderRadius: 10,
                  border: '1px solid rgba(15,15,30,0.1)',
                  fontSize: 13, fontFamily: 'inherit', resize: 'none',
                  background: '#FFFFFF',
                }}
              />
              <button
                data-testid="validation-modify-btn"
                onClick={doModify}
                disabled={!modifyInstruction.trim() || busy === 'modify'}
                style={{
                  width: '100%', marginTop: 8,
                  padding: '8px 14px', borderRadius: 10,
                  background: !modifyInstruction.trim() ? '#9CA3AF' : '#FFFFFF',
                  color: !modifyInstruction.trim() ? '#FFFFFF' : meta.accent,
                  fontWeight: 700, fontSize: 11,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  border: `1px solid ${meta.accent}`,
                  cursor: !modifyInstruction.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {busy === 'modify' ? <Loader2 size={12} style={{ animation: 'spin 0.9s linear infinite' }} /> : <Edit3 size={12} />}
                {busy === 'modify' ? `${meta.name} is revising…` : 'Apply modification'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer — the 3 sacred buttons */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid rgba(15,15,30,0.06)',
          background: '#FBFAF7',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {error && (
            <span style={{ fontSize: 11, color: '#EF4444', marginRight: 'auto' }}>{error}</span>
          )}
          {!error && irreversible && (
            <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#EF4444' }}>
              <AlertTriangle size={13} />
              <span>This action is <strong>irreversible</strong> · {action.replace('_', ' ')}</span>
            </div>
          )}
          {!error && !irreversible && (
            <span style={{ marginRight: 'auto', fontSize: 11, color: '#6B7280' }}>
              Safe action · stored locally, can be re-run anytime
            </span>
          )}
          <button
            data-testid="validation-cancel-btn"
            onClick={doCancel}
            disabled={!!busy}
            style={{
              padding: '10px 18px', borderRadius: 999,
              background: 'transparent', color: '#6B7280',
              fontWeight: 700, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              border: '1px solid rgba(15,15,30,0.12)', cursor: busy ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {busy === 'cancel' ? <Loader2 size={12} style={{ animation: 'spin 0.9s linear infinite' }} /> : <X size={12} />}
            Cancel
          </button>
          <button
            data-testid="validation-validate-btn"
            onClick={doValidate}
            disabled={!!busy}
            style={{
              padding: '10px 22px', borderRadius: 999,
              background: confirmStep ? '#EF4444' : meta.gradient,
              color: '#FFFFFF',
              fontWeight: 800, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              border: 'none', cursor: busy ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: confirmStep ? '0 0 0 4px #EF444430' : '0 6px 20px rgba(15,15,30,0.12)',
              transition: 'box-shadow 0.2s',
            }}
          >
            {busy === 'validate' ? <Loader2 size={13} style={{ animation: 'spin 0.9s linear infinite' }} /> :
              confirmStep ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}
            {confirmStep
              ? 'Confirm · this cannot be undone'
              : (ACTION_LABEL[action] || 'Validate')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== Smart Artifact Renderer ====================== */
function ArtifactBody({ artifact, contentText, accent }) {
  const kind = artifact?.kind;
  const content = artifact?.content;

  // 1) Outreach batch (Instagram DMs · cold emails · LinkedIn)
  if (kind === 'outreach_batch' && content && typeof content === 'object') {
    const recipients = Array.isArray(content.recipients) ? content.recipients : [];
    const tpl = content.message_template || '';
    const channel = (content.channel || 'social').toUpperCase();
    return (
      <div data-testid="artifact-outreach" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Message template card */}
        <div style={{
          padding: 14, background: '#FBFAF7',
          borderRadius: 12, border: `1px solid ${accent}30`,
          borderLeft: `3px solid ${accent}`,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
            color: accent, textTransform: 'uppercase', marginBottom: 6,
          }}>Message template · {channel}</div>
          <div style={{
            fontSize: 13, lineHeight: 1.6, color: '#0F0F1A',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'inherit',
          }}>{tpl || '(no template)'}</div>
          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 8, fontStyle: 'italic' }}>
            {'{{first_name}} '}is replaced per-recipient at send time.
          </div>
        </div>

        {/* Recipients list */}
        <div>
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
            color: '#6B7280', textTransform: 'uppercase', marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>Recipients · {recipients.length}</span>
            {content.provider && (
              <span style={{
                fontSize: 9, fontWeight: 800,
                background: '#EEF2FF', color: '#4338CA',
                padding: '2px 7px', borderRadius: 999,
                letterSpacing: '0.14em',
              }}>{String(content.provider).toUpperCase()}</span>
            )}
          </div>
          {recipients.length === 0 && (
            <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', padding: 12 }}>
              No recipients yet. Modify the brief to anchor on a sharper ICP segment, or wire a provider key.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recipients.map((r, i) => (
              <div key={i} data-testid={`recipient-${i}`} style={{
                padding: '8px 12px',
                background: '#FFFFFF',
                border: '1px solid rgba(15,15,30,0.08)',
                borderRadius: 8,
                display: 'grid',
                gridTemplateColumns: '24px 1fr auto',
                gap: 10, alignItems: 'center',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: `${accent}22`, color: accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800,
                }}>{i + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.name || r.first_name || '(unknown)'} {r.last_name || ''}
                    {r.title && <span style={{ color: '#6B7280', fontWeight: 500 }}> · {r.title}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.handle || r.email || r.company_domain || r.company || '—'}
                    {r.why_fit && <span style={{ fontStyle: 'italic' }}> · {r.why_fit}</span>}
                  </div>
                </div>
                {r.source && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: '#6B7280',
                    background: '#F4F3F0', padding: '2px 6px', borderRadius: 4,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}>{r.source}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {Array.isArray(content.warnings) && content.warnings.length > 0 && (
          <div style={{
            padding: 10, background: '#FEF3C7', color: '#92400E',
            border: '1px solid #FCD34D', borderRadius: 8, fontSize: 11,
          }}>
            ⚠ {content.warnings.join(' · ')}
          </div>
        )}
      </div>
    );
  }

  // 1.b) Single outreach deliverable (one recipient + one personalised message)
  if (kind === 'outreach_item' && content && typeof content === 'object') {
    const r = content.recipient || {};
    const msg = content.message || '';
    const channel = (content.channel || 'social').toUpperCase();
    const handle = r.handle || r.email || r.company_domain || r.company || '—';
    const name = (r.name || `${r.first_name || ''} ${r.last_name || ''}`).trim() || '(unknown)';
    return (
      <div data-testid="artifact-outreach-item" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Recipient card */}
        <div style={{
          padding: 14, background: '#FFFFFF',
          borderRadius: 12, border: '1px solid rgba(15,15,30,0.08)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: `${accent}22`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800,
          }}>{name[0]?.toUpperCase() || '?'}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F1A' }}>
              {name}{r.title ? <span style={{ color: '#6B7280', fontWeight: 500 }}> · {r.title}</span> : null}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {handle}{r.company && r.company !== handle ? ` · ${r.company}` : ''}
            </div>
            {r.why_fit && (
              <div style={{ fontSize: 11, color: '#4B4B5C', marginTop: 4, fontStyle: 'italic' }}>
                {r.why_fit}
              </div>
            )}
          </div>
          <span style={{
            fontSize: 9, fontWeight: 800, color: accent,
            background: `${accent}15`, padding: '4px 10px', borderRadius: 999,
            letterSpacing: '0.14em',
          }}>{channel}</span>
        </div>
        {/* Personalised message */}
        <div style={{
          padding: 16, background: '#FBFAF7',
          borderRadius: 12, border: `1px solid ${accent}30`,
          borderLeft: `3px solid ${accent}`,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
            color: accent, textTransform: 'uppercase', marginBottom: 8,
          }}>Personalised message</div>
          <div style={{
            fontSize: 14, lineHeight: 1.65, color: '#0F0F1A',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>{msg || '(no message)'}</div>
        </div>
        {content.parent_atom && (
          <div style={{
            fontSize: 11, color: '#6B7280', fontStyle: 'italic',
            padding: '6px 10px',
          }}>
            Part of: {content.parent_atom}
          </div>
        )}
      </div>
    );
  }

  // 2) Legacy lead_list
  if (kind === 'lead_list' && content && Array.isArray(content.leads)) {
    return <ArtifactBody artifact={{ kind: 'outreach_batch', content: { ...content, recipients: content.leads, channel: 'email' } }} accent={accent} contentText={contentText} />;
  }

  // 3) Content draft (Lyra)
  if (kind === 'content_draft' && content && typeof content === 'object') {
    return (
      <div data-testid="artifact-content-draft" style={{
        padding: 16, background: '#FBFAF7',
        borderRadius: 12, border: `1px solid ${accent}30`,
        borderLeft: `3px solid ${accent}`,
        fontSize: 14, lineHeight: 1.65, color: '#0F0F1A',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {content.draft || '(empty draft)'}
        {content.for_channel && (
          <div style={{ marginTop: 12, fontSize: 10, color: '#6B7280', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            For channel: {content.for_channel}
          </div>
        )}
      </div>
    );
  }

  // 4) Execution plan (Aria)
  if (kind === 'execution_plan' && content && typeof content === 'object') {
    const steps = Array.isArray(content.steps) ? content.steps : [];
    const files = Array.isArray(content.files_to_touch) ? content.files_to_touch : [];
    return (
      <div data-testid="artifact-plan" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', color: accent, textTransform: 'uppercase', marginBottom: 8 }}>
            Steps · {steps.length}
          </div>
          <ol style={{ margin: 0, paddingLeft: 22, fontSize: 13, lineHeight: 1.6, color: '#0F0F1A' }}>
            {steps.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
          </ol>
        </div>
        {files.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', color: '#6B7280', textTransform: 'uppercase', marginBottom: 6 }}>
              Files to touch
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {files.map((f, i) => (
                <code key={i} style={{
                  fontSize: 11, padding: '3px 8px',
                  background: '#F4F3F0', borderRadius: 6,
                  color: '#0F0F1A',
                  fontFamily: '"SF Mono", "JetBrains Mono", Menlo, monospace',
                }}>{f}</code>
              ))}
            </div>
          </div>
        )}
        {content.acceptance_check && (
          <div style={{ fontSize: 12, color: '#0F0F1A', padding: 10, background: '#ECFDF5', borderRadius: 8, borderLeft: '3px solid #10B981' }}>
            <strong style={{ color: '#065F46' }}>Acceptance:</strong> {content.acceptance_check}
          </div>
        )}
      </div>
    );
  }

  // Fallback — raw JSON / text
  return (
    <pre data-testid="artifact-preview" style={{
      margin: 0, padding: 16, background: '#FBFAF7',
      borderRadius: 12, border: '1px solid rgba(15,15,30,0.06)',
      fontSize: 12, lineHeight: 1.6,
      color: '#0F0F1A', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      fontFamily: '"SF Mono", "JetBrains Mono", Menlo, Consolas, monospace',
    }}>
      {contentText}
    </pre>
  );
}
